import {
  ResponseFacetValue,
  ResponseProduct,
  ResponseProductDinkus,
  ResponseTheme,
  ResponseThemeCollection,
  ResponseThemeProduct,
} from "./product";

export type PresentationSortingOption = {
  label: string;
  value: string;
};

export const PlpSortingOptions: PresentationSortingOption[] = [
  { label: "Recommended", value: "" },
  { label: "New", value: "-is_new" },
  { label: "Price: High to Low", value: "-price_lowest" },
  { label: "Price: Low to High", value: "price_lowest" },
];

export type PlpSelectedFacetOption = {
  id: string;
  name?: string;

  facetName: string;
  facetIsMultiSelect?: boolean;
};

// ---

export type PresentProductColorInfo = {
  key: string;
  value: string;
  isImage: boolean;
};

export type PresentationProduct = ResponseProduct & {
  presentationColors: PresentProductColorInfo[];
  presentationAutomaticallySelectedColorKey: string | undefined;
  fredhopperResponseId: string;
};

export type PresentationFacetPotentialOption = ResponseFacetValue;

export enum PresentationFacet_PresentationType {
  unspecified = "unspecified",
  colors = "colors",
}

export type PresentationFacet = {
  id: string;
  fredhopperResponseId: string;
  key: string;
  title: string;
  hasSubMenu: boolean;
  hasMultiSelection: boolean;
  presentationType?: PresentationFacet_PresentationType;
  potentialOptions: PresentationFacetPotentialOption[];
  isOpenByDefault?: boolean;
};

// ---

export type PresentationThemeProduct = ResponseThemeProduct & {
  presentationColors: PresentProductColorInfo[];
  presentationAutomaticallySelectedColorKey: string | undefined;
  fredhopperResponseId: string;
  fredhopperCampaignId: string;
};

export type PresentationTheme = ResponseTheme & {
  items: PresentationThemeProduct[] | any[];
};

export type PresentationThemeCollection = ResponseThemeCollection & {
  [key: string]: PresentationTheme;
};

// ---

export const getProductColorInfo = (
  product?: PresentationProduct | PresentationThemeProduct,
  selectedColor?: string
) => {
  let productColorInfo;
  if (product && selectedColor) {
    if (product.bbnt_color_info) {
      productColorInfo = product.bbnt_color_info.find(
        ({ id }) => id === selectedColor
      );
    }
  }

  if (product && !productColorInfo) {
    const productMagentoId = product.magento_id_product;
    if (product.bbnt_color_info) {
      const matchingColorInfo = product.bbnt_color_info.find(({ products }) =>
        products.some((productId) => productId === productMagentoId)
      );
      if (matchingColorInfo && !!matchingColorInfo.label) {
        productColorInfo = matchingColorInfo;
      } else {
        let firstColorBaseEntryValue: string | undefined;
        const productColorBaseEntries = Object.entries(
          product.colour_base || {}
        );
        if (productColorBaseEntries.length > 0) {
          const firstEntry = productColorBaseEntries[0];
          if (firstEntry.length > 1) {
            firstColorBaseEntryValue = firstEntry[1];
          }
        }

        if (firstColorBaseEntryValue) {
          productColorInfo = product.bbnt_color_info.find(
            ({ label }) => label === firstColorBaseEntryValue
          );
        }
      }
    }
  }

  return productColorInfo;
};

export const getImagesForProduct = (
  product?: PresentationProduct | PresentationThemeProduct,
  selectedColor?: string,
  isDefaultColorSelected = false
): string[] => {
  let images: string[] = [];

  const productColorInfo = getProductColorInfo(product, selectedColor);

  if (productColorInfo) {
    const thisColorsProducts = productColorInfo.products;
    if (thisColorsProducts && thisColorsProducts.length > 0) {
      const colorProductId = thisColorsProducts[0];

      if (product?.bbnt_gallery_images) {
        let matchingGalleryImages: string[] = [];
        for (const [productIdOfGallery, gallery] of Object.entries(
          product.bbnt_gallery_images
        )) {
          if (productIdOfGallery === colorProductId) {
            matchingGalleryImages = gallery;
          }
        }

        images = [...images, ...matchingGalleryImages];
      }
    }
  }

  if ((product as PresentationProduct)?.imageUrl) {
    if (isDefaultColorSelected && images.length > 0) {
      const imageUrlFitted = `${
        (product as PresentationProduct).imageUrl
      }?quality=100&bg-color=255,255,255&fit=crop&width=600&height=600&canvas=600:600&auto=webp`;

      // filter omitting domain
      const imageUrlChunks = imageUrlFitted.split('media')
      const imageUrlSuffix = imageUrlChunks.length === 2 ? imageUrlChunks[1] : '';

      images = images.filter((image) => {
        const imageChunks = image?.split('media')
        const imageSuffix = imageChunks.length === 2 ? imageChunks[1] : '';

        return !(imageSuffix && imageUrlSuffix && imageSuffix === imageUrlSuffix)
      });


      images = [...[imageUrlFitted], ...images];
    }
  }

  if (images.length < 1) {
    if ((product as PresentationProduct)?.imageUrl) {
      const imageUrlFitted = `${
        (product as PresentationProduct).imageUrl
      }?quality=100&bg-color=255,255,255&fit=crop&width=600&height=600&auto=webp`;
      images = [imageUrlFitted];
    } else if ((product as PresentationThemeProduct)?._thumburl) {
      const imageUrlThumb = `${
        (product as PresentationThemeProduct)._thumburl
      }?quality=100&bg-color=255,255,255&fit=crop&width=600&height=600&auto=webp`;
      images = [imageUrlThumb];
    }
  }

  return images;
};

export const getUrlForProduct = (
  product?: PresentationProduct | PresentationThemeProduct,
  selectedColor?: string
) => {
  const productColorInfo = getProductColorInfo(product, selectedColor);

  let url;
  if ((product as PresentationProduct)?.url) {
    url = (product as PresentationProduct).url;
  } else if ((product as PresentationThemeProduct)?.page_url) {
    const { page_url, url_prefix } = product as PresentationThemeProduct;

    let prefix = "/";
    if (url_prefix) {
      prefix = `/${url_prefix}/`;
    }
    url = `${prefix}${page_url || ""}`;
  }
  if (productColorInfo) {
    const thisColorsProducts = productColorInfo.products;
    if (thisColorsProducts && thisColorsProducts.length > 0) {
      const colorProductId = thisColorsProducts[0];

      if (product?.bbnt_product_urls) {
        for (const [productIdOfUrl, thisUrl] of Object.entries(
          product.bbnt_product_urls
        )) {
          if (productIdOfUrl === colorProductId) {
            url = thisUrl;
            break;
          }
        }
      }
    }
  }

  return url;
};

export const getHeadingForProduct = (
  product?: PresentationProduct | PresentationThemeProduct,
  selectedColor?: string
) => {
  const productColorInfo = getProductColorInfo(product, selectedColor);

  const isString = (value: any) => {
    return typeof value === "string" || value instanceof String;
  };
  const getFirstKeyedValue = (input: { [key: string]: string }) => {
    let firstKeyedValue;
    for (const [key, value] of Object.entries(input)) {
      if (!firstKeyedValue) {
        firstKeyedValue = value;
        break;
      }
    }
    return firstKeyedValue;
  };
  let heading: string =
    (isString(product?.heading || "")
      ? (product as PresentationProduct)?.heading
      : getFirstKeyedValue((product as PresentationThemeProduct)?.heading)) ||
    "";
  if (productColorInfo) {
    const thisColorsProducts = productColorInfo.products;
    if (thisColorsProducts && thisColorsProducts.length > 0) {
      const colorProductId = thisColorsProducts[0];

      if (product?.bbnt_product_titles) {
        for (const [productIdOfHeading, thisHeading] of Object.entries(
          product.bbnt_product_titles
        )) {
          if (productIdOfHeading === colorProductId) {
            heading = thisHeading;
            break;
          }
        }
      }
    }
  }

  return heading;
};

export const getDinkusForProduct = (
  product?: PresentationProduct | PresentationThemeProduct,
  selectedColor?: string
): ResponseProductDinkus => {
  const productColorInfo = getProductColorInfo(product, selectedColor);

  let productIdOfInterest = null;
  if (productColorInfo) {
    const thisColorsProducts = productColorInfo.products;
    if (thisColorsProducts && thisColorsProducts.length > 0) {
      const colorProductId = thisColorsProducts[0];
      if (colorProductId) {
        productIdOfInterest = colorProductId;
      }
    }
  } else if (product?.bbnt_product_ids && product.bbnt_product_ids.length > 0) {
    const firstProductId = product.bbnt_product_ids[0];
    if (firstProductId) {
      productIdOfInterest = firstProductId;
    }
  }

  let dinkus: ResponseProductDinkus = {};
  if (productIdOfInterest && product?.bbnt_product_dinkus) {
    for (const [productIdOfDinkus, thisDinkus] of Object.entries(
      product.bbnt_product_dinkus
    )) {
      if (productIdOfDinkus === productIdOfInterest) {
        dinkus = thisDinkus;
        break;
      }
    }
  }

  return dinkus;
};
